import React from 'react';
import { PLAY_STORE_URL, APP_STORE_URL } from './AppDownloadPopup';
import appQr from '../assets/images/Kopartner.png';

const PREVIEWS = [
  { title: 'Discover', subtitle: 'Find partners', gradient: 'from-violet-500 to-purple-700', emoji: '🎬' },
  { title: 'Book', subtitle: 'Easy booking', gradient: 'from-fuchsia-500 to-pink-600', emoji: '📅' },
  { title: 'Chat', subtitle: 'Stay connected', gradient: 'from-sky-500 to-blue-700', emoji: '💬' },
  { title: 'Trusted', subtitle: 'Verified KPs', gradient: 'from-emerald-500 to-teal-600', emoji: '✅' },
  { title: 'Pay', subtitle: 'Secure payments', gradient: 'from-amber-400 to-orange-500', emoji: '💳' },
  { title: 'Rated', subtitle: 'Real reviews', gradient: 'from-rose-500 to-red-600', emoji: '⭐' },
];

/**
 * Homepage section above FAQ — Download the App Now
 * 6 compact phone previews to use less vertical space
 */
const AppDownloadSection = () => {
  return (
    <section
      id="download-app"
      className="py-10 px-4 bg-gradient-to-br from-slate-900 via-purple-950 to-fuchsia-950 text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-pink-500 blur-3xl" />
        <div className="absolute bottom-0 left-10 w-64 h-64 rounded-full bg-purple-500 blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-6">
          <p className="text-pink-300 text-xs font-semibold tracking-widest uppercase mb-1.5">
            For Partners & Clients
          </p>
          <h2 className="text-2xl md:text-4xl font-bold mb-2">
            Download the App Now
          </h2>
          <p className="text-purple-100/90 text-sm md:text-base max-w-xl mx-auto">
            Browse, book, manage bookings, and get instant notifications on your phone.
          </p>
        </div>

        {/* 6 compact phones — 3 cols mobile, 6 cols desktop, tighter gap */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 mb-8 max-w-6xl mx-auto">
          {PREVIEWS.map(({ title, subtitle, gradient, emoji }) => (
            <div key={title} className="flex justify-center">
              <div className="w-full max-w-[110px] sm:max-w-[180px] rounded-[1.1rem] border-[3px] border-white/20 bg-black/40 shadow-lg overflow-hidden">
                <div className="h-4 bg-black/50 flex items-center justify-center">
                  <div className="w-8 h-1 rounded-full bg-white/30" />
                </div>
                <div className={`aspect-[9/16] bg-gradient-to-br ${gradient} p-2.5 flex flex-col`}>
                  <div className="text-lg sm:text-2xl mb-1">{emoji}</div>
                  <p className="font-bold text-[11px] sm:text-lg leading-tight">{title}</p>
                  <p className="text-[9px] sm:text-[14px] text-white/85 mt-0.5 leading-tight">{subtitle}</p>
                  <div className="mt-auto space-y-1">
                    <div className="h-1 rounded bg-white/25 w-full" />
                    <div className="h-1 rounded bg-white/20 w-3/4" />
                    <div className="h-4 rounded-md bg-white/30 mt-1.5" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
          <div className="bg-white rounded-xl p-3 shadow-xl">
            <img
              src={appQr}
              alt="QR code to download KoPartner"
              className="w-28 h-28 object-contain"
            />
            <p className="text-center text-[10px] text-gray-600 mt-1.5 font-medium">Scan to download</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-black hover:bg-gray-900 text-white px-5 py-3 rounded-xl border border-white/20 transition shadow-lg"
            >
              <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current" aria-hidden>
                <path d="M3.18 23.76c.1.05.21.07.32.07.17 0 .33-.06.46-.18l7.55-7.55-2.1-2.1L3.18 23.76zM1.01 1.53C.38 2.18 0 3.12 0 4.27v15.46c0 1.15.38 2.09 1.01 2.74l9.48-9.48L1.01 1.53zM21.46 10.3l-2.72-1.57-3.07 3.07 3.07 3.07 2.75-1.59c.95-.55.95-1.44-.03-2.98zM9.4 12.8l2.1 2.1 7.55-7.55c-.13-.12-.29-.18-.46-.18-.11 0-.22.02-.32.07L9.4 12.8z" />
              </svg>
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-wide text-gray-300">Get it on</div>
                <div className="text-base font-semibold leading-tight">Google Play</div>
              </div>
            </a>

            {APP_STORE_URL ? (
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-black hover:bg-gray-900 text-white px-5 py-3 rounded-xl border border-white/20 transition shadow-lg"
              >
                <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current" aria-hidden>
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.22-1.98 1.09-3.13-1.05.04-2.31.7-3.06 1.58-.67.77-1.26 2-1.1 3.17 1.16.09 2.35-.59 3.07-1.62" />
                </svg>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-wide text-gray-300">Download on the</div>
                  <div className="text-base font-semibold leading-tight">App Store</div>
                </div>
              </a>
            ) : (
              <div className="inline-flex items-center gap-3 bg-black/50 text-white/70 px-5 py-3 rounded-xl border border-white/10 cursor-not-allowed">
                <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current" aria-hidden>
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.22-1.98 1.09-3.13-1.05.04-2.31.7-3.06 1.58-.67.77-1.26 2-1.1 3.17 1.16.09 2.35-.59 3.07-1.62" />
                </svg>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-wide">Coming Soon</div>
                  <div className="text-base font-semibold leading-tight">App Store</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppDownloadSection;
